import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, FileSignature, Send, ShieldCheck, X, Eye, Ban, FileText, Trash2 } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, StatusChip, EmptyState } from "@/components/Common";
import { toast } from "sonner";

const COD_RET = {
  "1": [
    { code: "303", label: "Honorarios profesionales 10%" },
    { code: "304", label: "Servicios predomina intelecto 8%" },
    { code: "307", label: "Servicios predomina mano de obra 2%" },
    { code: "310", label: "Servicios entre sociedades 2%" },
    { code: "312", label: "Transferencia de bienes muebles 1.75%" },
    { code: "320", label: "Arrendamiento mercantil 10%" },
    { code: "322", label: "Seguros y reaseguros 1%" },
    { code: "332", label: "Otras retenciones varias 2%" },
  ],
  "2": [
    { code: "721", label: "IVA bienes 30%" },
    { code: "725", label: "IVA servicios 70%" },
    { code: "727", label: "IVA servicios profesionales 100%" },
    { code: "723", label: "IVA arrendamientos 30%" },
  ],
};

export default function Retentions() {
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/retentions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);

  const action = async (id, path) => {
    try {
      await api.post(`/retentions/${id}/${path}`);
      toast.success(`Operación '${path}' completada.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="retentions-page">
      <PageHeader
        title="Comprobantes de Retención"
        subtitle="Retenciones de IVA y Renta a proveedores (SRI cod 07)."
        testid="retentions-header"
        action={<button data-testid="retentions-create-button" onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Nueva retención</button>}
      />

      {showForm && <RetentionForm suppliers={suppliers} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}

      {items.length === 0 ? (
        <EmptyState title="Sin retenciones" hint="Crea retenciones al pagar facturas de proveedores." testid="retentions-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="retentions-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
              <th className="px-5 py-3">Número</th><th className="px-5 py-3">Proveedor</th>
              <th className="px-5 py-3">Período</th><th className="px-5 py-3">Doc. Sustento</th>
              <th className="px-5 py-3 text-right">Total retenido</th>
              <th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-3 font-mono">{n.document_number}</td>
                  <td className="px-5 py-3">{n.supplier_name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{n.periodo_fiscal}</td>
                  <td className="px-5 py-3 font-mono text-xs">{n.doc_sustento_numero}</td>
                  <td className="px-5 py-3 text-right font-bold">$ {Number(n.total_retenido).toFixed(2)}</td>
                  <td className="px-5 py-3"><StatusChip status={n.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <a title="PDF" href={`${API_BASE}/retentions/${n.id}/pdf`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-[#F3F2ED] inline-flex"><FileText className="w-4 h-4 text-[#5C6B64]" /></a>
                      {n.status === "borrador" && <button title="Firmar" onClick={() => action(n.id, "sign")} className="p-2 rounded hover:bg-[#DDE6ED]"><FileSignature className="w-4 h-4 text-[#608090]" /></button>}
                      {(n.status === "firmada" || n.status === "rechazada") && <button title="Enviar" onClick={() => action(n.id, "send")} className="p-2 rounded hover:bg-[#DDE6ED]"><Send className="w-4 h-4 text-[#608090]" /></button>}
                      {["firmada","enviada","rechazada"].includes(n.status) && <button title="Autorizar" onClick={() => action(n.id, "authorize")} className="p-2 rounded hover:bg-[#E4EFE6]"><ShieldCheck className="w-4 h-4 text-[#507A59]" /></button>}
                      {n.status !== "anulada" && <button title="Anular" onClick={() => { if (window.confirm("¿Anular retención?")) action(n.id, "cancel"); }} className="p-2 rounded hover:bg-[#F3DADA]"><Ban className="w-4 h-4 text-[#A44A4A]" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RetentionForm({ suppliers, onClose, onCreated }) {
  const [supplier_id, setSupplierId] = useState("");
  const today = new Date();
  const [periodo, setPeriodo] = useState(`${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`);
  const [doc_num, setDocNum] = useState("");
  const [doc_fecha, setDocFecha] = useState(today.toISOString().slice(0, 10));
  const [doc_total, setDocTotal] = useState(0);
  const uidRef = useRef(0);
  const newImp = (base) => ({ uid: ++uidRef.current, ...base });
  const [impuestos, setImpuestos] = useState(() => [newImp({ codigo: "2", codigo_retencion: "721", base_imponible: 0, porcentaje_retener: 30 })]);
  const [saving, setSaving] = useState(false);

  const totalRetenido = useMemo(
    () => impuestos.reduce((sum, r) => sum + (Number(r.base_imponible) || 0) * (Number(r.porcentaje_retener) || 0) / 100, 0),
    [impuestos],
  );

  const setImp = (i, patch) => { const n = impuestos.slice(); n[i] = { ...n[i], ...patch }; setImpuestos(n); };
  const addImp = () => setImpuestos([...impuestos, newImp({ codigo: "1", codigo_retencion: "312", base_imponible: 0, porcentaje_retener: 1.75 })]);
  const rmImp = (i) => setImpuestos(impuestos.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!supplier_id) return toast.error("Selecciona un proveedor");
    if (!doc_num || !/^\d{3}-\d{3}-\d{9}$/.test(doc_num)) return toast.error("Formato número doc sustento: 001-001-000000001");
    setSaving(true);
    try {
      await api.post("/retentions", {
        supplier_id, periodo_fiscal: periodo,
        doc_sustento_tipo: "01", doc_sustento_numero: doc_num,
        doc_sustento_fecha: new Date(doc_fecha).toISOString(),
        doc_sustento_total: Number(doc_total),
        impuestos: impuestos.map((r) => ({
          codigo: r.codigo, codigo_retencion: r.codigo_retencion,
          base_imponible: Number(r.base_imponible), porcentaje_retener: Number(r.porcentaje_retener),
        })),
      });
      toast.success("Retención creada");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="retention-form" className="card-soft p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold">Nueva retención</h3>
        <button onClick={onClose} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Proveedor *</span>
            <select data-testid="retention-supplier-select" required className="input-soft" value={supplier_id} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">Seleccione</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.business_name} ({s.ruc})</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Período fiscal (MM/YYYY) *</span>
            <input required pattern="\d{2}/\d{4}" className="input-soft" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Factura sustento Nº *</span>
            <input required placeholder="001-001-000000001" className="input-soft" value={doc_num} onChange={(e) => setDocNum(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Fecha emisión sustento *</span>
            <input required type="date" className="input-soft" value={doc_fecha} onChange={(e) => setDocFecha(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Total factura sustento ($)</span>
            <input type="number" step="0.01" className="input-soft" value={doc_total} onChange={(e) => setDocTotal(e.target.value)} />
          </label>
        </div>

        <div className="border border-[#E2E0D8] rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 p-3 bg-[#F3F2ED] text-[11px] uppercase tracking-[0.16em] text-[#5C6B64]">
            <div className="col-span-2">Impuesto</div>
            <div className="col-span-4">Concepto</div>
            <div className="col-span-2">Base imponible</div>
            <div className="col-span-1">%</div>
            <div className="col-span-2 text-right">Retenido</div>
            <div className="col-span-1"></div>
          </div>
          {impuestos.map((r, idx) => {
            const opts = COD_RET[r.codigo] || [];
            const valor = (Number(r.base_imponible) || 0) * (Number(r.porcentaje_retener) || 0) / 100;
            return (
              <div key={r.uid} className="grid grid-cols-12 gap-2 p-3 border-t border-[#E2E0D8] items-center">
                <select className="input-soft col-span-2" value={r.codigo} onChange={(e) => setImp(idx, { codigo: e.target.value, codigo_retencion: (COD_RET[e.target.value] || [{}])[0].code || "" })}>
                  <option value="1">Renta</option><option value="2">IVA</option>
                </select>
                <select className="input-soft col-span-4" value={r.codigo_retencion} onChange={(e) => {
                  const opt = opts.find((o) => o.code === e.target.value);
                  const pct = opt ? Number(opt.label.match(/(\d+(?:\.\d+)?)%/)?.[1] || r.porcentaje_retener) : r.porcentaje_retener;
                  setImp(idx, { codigo_retencion: e.target.value, porcentaje_retener: pct });
                }}>
                  {opts.map((o) => <option key={o.code} value={o.code}>{o.code} — {o.label}</option>)}
                </select>
                <input type="number" step="0.01" className="input-soft col-span-2" value={r.base_imponible} onChange={(e) => setImp(idx, { base_imponible: e.target.value })} />
                <input type="number" step="0.01" className="input-soft col-span-1" value={r.porcentaje_retener} onChange={(e) => setImp(idx, { porcentaje_retener: e.target.value })} />
                <div className="col-span-2 text-right font-bold">$ {valor.toFixed(2)}</div>
                <div className="col-span-1 flex justify-end">
                  {impuestos.length > 1 && <button type="button" onClick={() => rmImp(idx)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>}
                </div>
              </div>
            );
          })}
          <div className="p-3 border-t border-[#E2E0D8] bg-[#FAFAF8] flex items-center justify-between">
            <button type="button" onClick={addImp} className="btn-secondary text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Agregar retención</button>
            <div className="font-heading text-xl font-black">Total retenido: $ {totalRetenido.toFixed(2)}</div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button data-testid="retention-save-button" disabled={saving} className="btn-primary" type="submit">{saving ? "Guardando..." : "Crear retención"}</button>
        </div>
      </form>
    </div>
  );
}
